import { getBackendBaseUrl, getLoginPath } from './config';
import { clearAuthToken, getAuthHeaders } from './auth';

export function getAccountingApiRoot(): string {
  const base = getBackendBaseUrl();
  return base ? `${base}/api/panel/accounting` : '/api/panel/accounting';
}


export interface AccountingHealth {
  module: string;
  version: string;
  database_connected: boolean;
  message: string;
}

export interface AccountingSummary {
  title: string;
  subtitle: string;
  kpi: {
    period_label: string;
    income: number;
    expense: number;
    balance: number;
  };
  notice: string;
}

export interface MenuSection {
  id: string;
  title: string;
  description: string;
  icon: string;
  available: boolean;
}

export interface LookupProject {
  id: number;
  label: string;
  status: string;
  final_amount?: number;
  initial_estimate?: number;
  progress?: number;
  priority?: string;
  accounting_expense_total?: number;
  accounting_line_count?: number;
}

export interface LookupExpenseType {
  id: number;
  title: string;
  note?: string;
  is_active: boolean;
}

export interface ExpenseTypePayload {
  title: string;
  note?: string;
  is_active?: boolean;
}

export interface ExpenseTypeReportSummaryRow {
  typeId: string;
  typeTitle: string;
  count: number;
  totalAmount: number;
}

export interface ExpenseTypeReportEntryRow {
  id: string;
  dateFa: string;
  beneficiary: string;
  projectLabel: string;
  typeId: string;
  typeTitle: string;
  paymentMethod: string;
  amount: number;
  description: string;
}

/** ردیف سند حسابداری مرتبط با یک پروژه (از API واقعی) */
export interface ProjectAccountingDocument {
  id: string;
  docNo: string;
  docDate: string;
  operationDescription: string;
  totalDebit: number;
  totalCredit: number;
  projectAmount: number;
  lines: ProjectDocumentLineRow[];
}

export interface ProjectDocumentLineRow {
  id: string;
  lineId: string;
  documentId: string;
  docNo: string;
  dateFa: string;
  accountCode: string;
  accountTitle: string;
  beneficiaryKey: string;
  beneficiary: string;
  expenseTypeId: string;
  expenseTypeTitle: string;
  paymentMethod: string;
  amount: number;
  debit: number;
  credit: number;
  description: string;
}

export interface ProjectBankStatementRow {
  id: number;
  transaction_date: string | null;
  transaction_time: string | null;
  doc_no: string | null;
  description: string | null;
  credit: number;
  debit: number;
  direction: 'incoming' | 'outgoing' | null;
  expense_type_id?: number | null;
  expense_type_title?: string | null;
  counterparty_type: string | null;
  counterparty_id: number | null;
  counterparty_label: string | null;
  beneficiary_key: string;
  notes: string | null;
  bank_account_number: string | null;
  registered_at: string | null;
}

export interface ProjectBankStatementsResponse {
  items: ProjectBankStatementRow[];
  summary: {
    total_credit: number;
    total_debit: number;
    count: number;
  };
}

export interface ProjectAdvanceRow {
  id: number;
  employee_id: number;
  employee_name: string;
  beneficiary_key: string;
  amount: number;
  payment_type: 'advance' | 'on_account' | 'other' | string;
  payment_type_label: string;
  expense_type_id?: number | null;
  expense_type_title?: string | null;
  payment_status?: string | null;
  registered_at?: string | null;
  date_fa?: string | null;
  payroll_month?: string | null;
  notes?: string | null;
  description?: string | null;
  bsi_statement_registration_id?: number | null;
}

export interface ProjectAdvancesResponse {
  items: ProjectAdvanceRow[];
  summary: {
    total_amount: number;
    count: number;
  };
}

export interface LookupBeneficiary {
  id: string;
  name: string;
  type: 'client' | 'company' | 'employee';
}

export interface LookupBankAccount {
  id: number;
  name: string;
  bank_name: string;
  account_number?: string | null;
  card_number?: string | null;
  sheba_number?: string | null;
  account_holder_name: string;
  account_type: 'company' | 'personal' | 'project';
  is_active: boolean;
  is_default_destination?: boolean;
  current_balance?: number | string | null;
}

export interface BeneficiaryBankAccount {
  id: string;
  account_id: number;
  label: string;
  account_holder_name?: string | null;
  account_number?: string | null;
  card_number?: string | null;
  sheba_number?: string | null;
  is_default: boolean;
}

export interface BankAccountTransaction {
  id: number;
  bank_account_id: number;
  direction: 'debit' | 'credit';
  amount: number;
  balance_before: number;
  balance_after: number;
  source_type?: string | null;
  source_id?: number | null;
  description?: string | null;
  created_at?: string;
}

export interface AccountingBankMovementRecord {
  id: string;
  documentId?: string | null;
  documentLineId?: string | null;
  direction: 'incoming' | 'outgoing' | 'transfer';
  amount: number;
  valueDate?: string | null;
  sourceBankAccountId?: number | null;
  destinationBankAccountId?: number | null;
  counterpartyType?: 'employee' | 'client' | 'company' | '' | null;
  counterpartyId?: number | null;
  counterpartyBankAccountId?: number | null;
  paymentMethod?: 'card' | 'sheba' | 'account' | 'cash' | 'cheque' | '' | null;
  referenceNo?: string | null;
  description?: string | null;
  createdAt?: string | null;
}

export interface BankAccountPayload {
  name: string;
  bank_name: string;
  account_holder_name: string;
  account_type: 'company' | 'personal' | 'project';
  account_number?: string;
  card_number?: string;
  sheba_number?: string;
  branch_name?: string;
  account_holder_national_code?: string;
  description?: string;
  current_balance?: number | string;
  is_active?: boolean;
}

async function getJson<T>(path: string): Promise<T> {
  const r = await fetch(`${getAccountingApiRoot()}${path}`, {
    headers: getAuthHeaders(),
  });
  if (r.status === 401 && typeof window !== 'undefined') {
    clearAuthToken();
    window.location.href = getLoginPath();
    return new Promise(() => {});
  }
  const text = await r.text();
  if (!r.ok) {
    let msg = `خطا: ${r.status}`;
    try {
      if (text) {
        const j = JSON.parse(text) as { message?: string; error?: string };
        msg = j.message ?? j.error ?? msg;
      }
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (!text?.trim()) return {} as T;
  return JSON.parse(text) as T;
}

async function sendJson<T>(method: 'POST' | 'PUT' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getAccountingApiRoot()}${path}`, {
    method,
    headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
    body: body ? JSON.stringify(body) : undefined,
  });
  if (r.status === 401 && typeof window !== 'undefined') {
    clearAuthToken();
    window.location.href = getLoginPath();
    return new Promise(() => {});
  }
  const text = await r.text();
  if (!r.ok) {
    let msg = `خطا: ${r.status}`;
    try {
      if (text) {
        const j = JSON.parse(text) as { message?: string; error?: string };
        msg = j.message ?? j.error ?? msg;
      }
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  if (!text?.trim()) return {} as T;
  return JSON.parse(text) as T;
}


export interface EmployeeRecord {
  id: number;
  first_name: string;
  last_name: string;
  full_name?: string;
  employee_code?: string | null;
  national_code?: string | null;
  insurance_code?: string | null;
  birth_date?: string | null;
  mobile?: string | null;
  phone?: string | null;
  status?: 'active' | 'vacation' | 'inactive' | 'terminated' | string;
  /** حداقل یک حساب/کارت بانکی غیرفعال دارد */
  has_inactive_bank_account?: boolean;
  created_at?: string;
}

export interface EmployeePayload {
  first_name: string;
  last_name: string;
  national_code?: string | null;
  insurance_code?: string;
  birth_date?: string;
  mobile?: string;
  phone?: string;
  status?: 'active' | 'vacation' | 'inactive' | 'terminated';
}

export interface BankOption {
  id: number;
  name: string;
}

export interface EmployeeBankAccountRecord {
  id: number;
  employee_id: number;
  bank_id: number;
  account_holder_name: string;
  account_number?: string | null;
  card_number?: string | null;
  iban?: string | null;
  notes?: string | null;
  is_default: boolean;
  is_active: boolean;
  bank?: {
    id: number;
    name: string;
  } | null;
}

export interface EmployeeBankAccountPayload {
  bank_id: number;
  account_holder_name: string;
  account_number?: string;
  card_number?: string;
  iban?: string;
  notes?: string;
  is_default?: boolean;
  is_active?: boolean;
}

export interface CardBankInfo {
  bank_id?: number | null;
  bank_name?: string | null;
  iban?: string | null;
  account_holder_name?: string | null;
}

export interface EmployeeAccountingLine {
  lineId: string;
  documentId: string;
  docNo: string;
  docDate: string;
  operationDescription: string;
  projectId: number | null;
  projectLabel: string;
  accountCode: string;
  accountTitle: string;
  expenseTypeId: string;
  expenseTypeTitle: string;
  paymentMethod: string;
  debit: number;
  credit: number;
  amount: number;
}

export interface EmployeeProjectSummary {
  projectId: number;
  projectLabel: string;
  total_debit: number;
  total_credit: number;
  balance: number;
  line_count: number;
}

export interface EmployeeAccountingProfile {
  employee: EmployeeRecord;
  summary: {
    total_debit: number;
    total_credit: number;
    balance: number;
    line_count: number;
  };
  projects: EmployeeProjectSummary[];
  lines: EmployeeAccountingLine[];
}

export type AccountingHeadingLevel = 'general' | 'subsidiary' | 'detail';

export interface AccountingHeadingNode {
  id: number;
  code: string;
  title: string;
  level: AccountingHeadingLevel;
  parent_id?: number | null;
  allowed_subsidiary_type?: 'employee' | 'client' | 'company' | 'project' | null;
  linked_entity_type?: 'employee' | 'client' | 'company' | 'project' | null;
  linked_entity_id?: number | null;
  linked_entity_label?: string | null;
  is_active: boolean;
  mahiyat_code?: number | null;
  type_code?: number | null;
  header_code?: number | null;
  children: AccountingHeadingNode[];
}

export interface AccountingHeadingListItem {
  id: number;
  code: string;
  title: string;
  level: AccountingHeadingLevel | 'project';
  parent_id?: number | null;
  allowed_subsidiary_type?: 'employee' | 'client' | 'company' | 'project' | null;
  linked_entity_type?: 'employee' | 'client' | 'company' | 'project' | null;
  linked_entity_id?: number | null;
  linked_entity_label?: string | null;
  is_active: boolean;
  mahiyat_code?: number | null;
  type_code?: number | null;
  header_code?: number | null;
  total_debit: number;
  total_credit: number;
  balance: number;
  last_doc_no: string;
  last_doc_date: string;
  entity_id?: number;
  status?: string;
  status_label?: string;
}

export interface AccountingSubsidiary {
  id: number;
  title: string;
  entityType: 'employee' | 'client' | 'company' | 'project';
  entityId: number;
  isActive: boolean;
}

export interface AccountingDocumentRowPayload {
  account_code?: string;
  account_title?: string;
  project_id?: number | null;
  expense_type_id?: number | null;
  subsidiary_id?: number | null;
  counterparty_type?: 'employee' | 'client' | 'company' | null;
  counterparty_id?: number | null;
  counterparty_bank_account_id?: number | null;
  source_bank_account_id?: number | null;
  destination_bank_account_id?: number | null;
  payment_method?: 'account' | 'card' | 'sheba' | 'cash' | 'cheque' | null;
  bank_amount?: number;
  reference_no?: string;
  debit?: number;
  credit?: number;
}

function unwrapList<T>(body: { success?: boolean; data?: T } | T): T {
  if (body && typeof body === 'object' && 'data' in body && (body as { data?: T }).data !== undefined) {
    return (body as { data: T }).data;
  }
  return body as T;
}

export interface AccountingDocumentRecord {
  id: string;
  docNo: string;
  verificationCode?: string;
  docDate: string;
  operationDescription: string;
  totalDebit: number;
  totalCredit: number;
  difference: number;
  createdAt: string;
  projectIds?: number[];
  projectLabels?: string[];
  rows?: Array<{
    id: string;
    accountCode: string;
    accountTitle: string;
    projectId?: number | null;
    expenseTypeId?: number | null;
    subsidiaryId?: number | null;
    counterpartyType?: 'employee' | 'client' | 'company' | null;
    counterpartyId?: number | null;
    counterpartyName?: string | null;
    /** بدهکار یا بستانکار — بر اساس مبلغ همان ردیف */
    counterpartySide?: 'debit' | 'credit' | null;
    counterpartyBankAccountId?: number | null;
    sourceBankAccountId?: number | null;
    destinationBankAccountId?: number | null;
    paymentMethod?: 'account' | 'card' | 'sheba' | 'cash' | 'cheque' | null;
    bankAmount?: number | null;
    referenceNo?: string | null;
    debit: number;
    credit: number;
  }>;
}

export interface AccountingAccountReportRow {
  documentId: string;
  docNo: string;
  docDate: string;
  operationDescription: string;
  lineId: string;
  lineNo: number;
  accountCode: string;
  accountTitle: string;
  projectId?: number | null;
  debit: number;
  credit: number;
}

export interface PayrollCalculatorProjectOption {
  id: number;
  label: string;
  status: string;
  has_attendance: boolean;
  attendance_count: number;
  personnel_count: number;
}

export interface PayrollBankAccountOption {
  id: number;
  bank_name: string;
  account_number?: string | null;
  iban?: string | null;
  is_default: boolean;
  label: string;
}

export interface PayrollCalculatorRow {
  row_key: string;
  employee_id: number;
  employee_name: string;
  employee_code?: string | null;
  insurance_code?: string | null;
  project_id: number;
  project_name: string;
  salary_type: 'monthly' | 'daily';
  salary_type_label: string;
  base_salary: number;
  daily_salary: number;
  working_days_per_month: number;
  total_days_in_month: number;
  present_days: number;
  absent_days: number;
  half_days: number;
  late_days: number;
  vacation_days: number;
  sick_leave_days: number;
  worked_days: number;
  working_minutes: number;
  overtime_hours: number;
  worked_salary: number;
  absence_deduction: number;
  overtime_pay: number;
  advance_deduction: number;
  incentive: number;
  gross_salary: number;
  net_salary: number;
  bank_accounts?: PayrollBankAccountOption[];
  selected_bank_account_id?: number | null;
  bank_name?: string | null;
  account_number?: string | null;
  iban?: string | null;
}

export interface PayrollCalculatorPreview {
  month: string;
  month_label: string;
  date_range_label: string;
  project_ids: number[];
  rows: PayrollCalculatorRow[];
  totals: {
    employee_count: number;
    row_count: number;
    total_gross: number;
    total_net: number;
    total_overtime_hours: number;
  };
  read_only: boolean;
  warning?: string;
}

export interface PayrollCalculatorEditableRow extends PayrollCalculatorRow {
  overtime_hourly_rate: number;
  manual_advance_deduction: number;
  manual_incentive: number;
  computed_net: number;
  net_mode: 'auto' | 'manual' | 'formula';
  net_formula?: string | null;
  bank_accounts: PayrollBankAccountOption[];
  selected_bank_account_id: number | null;
  /** خارج از محاسبه، اکسل و سند — قابل بازگردانی */
  excluded?: boolean;
}

export interface AccountingAccountReport {
  accountCode: string;
  accountTitle?: string | null;
  accountLevel?: string | null;
  includeSubAccounts?: boolean;
  fromDate?: string | null;
  toDate?: string | null;
  totalDebit: number;
  totalCredit: number;
  balance: number;
  rows: AccountingAccountReportRow[];
}

export const accountingApi = {
  async health(): Promise<AccountingHealth> {
    const res = await getJson<{ success: boolean; data: AccountingHealth }>('/health');
    return res.data;
  },

  async summary(): Promise<AccountingSummary> {
    const res = await getJson<{ success: boolean; data: AccountingSummary }>('/summary');
    return res.data;
  },

  async menuSections(): Promise<MenuSection[]> {
    const res = await getJson<{ success: boolean; data: { sections: MenuSection[] } }>(
      '/menu-sections',
    );
    return res.data.sections;
  },

  async lookups(): Promise<{
    projects: LookupProject[];
    beneficiaries: LookupBeneficiary[];
    bank_accounts: LookupBankAccount[];
    expense_types: LookupExpenseType[];
  }> {
    const res = await getJson<{
      success: boolean;
      data: {
        projects: LookupProject[];
        beneficiaries: LookupBeneficiary[];
        bank_accounts: LookupBankAccount[];
        expense_types: LookupExpenseType[];
      };
    }>('/lookups');
    const data = unwrapList(res);
    return (
      data && typeof data === 'object' && 'projects' in data
        ? (data as {
            projects: LookupProject[];
            beneficiaries: LookupBeneficiary[];
            bank_accounts: LookupBankAccount[];
            expense_types: LookupExpenseType[];
          })
        : { projects: [], beneficiaries: [], bank_accounts: [], expense_types: [] }
    );
  },

  /** لیست پروژه‌ها از منابع موجود روی production (بدون GET /projects که هنوز 404 است) */
  async projects(): Promise<LookupProject[]> {
    const errors: string[] = [];

    try {
      const payroll = await this.payrollCalculatorProjects('');
      if (payroll?.projects?.length) {
        return payroll.projects.map((p) => ({
          id: p.id,
          label: p.label,
          status: p.status || '',
          progress: 0,
        }));
      }
    } catch (e) {
      errors.push(e instanceof Error ? e.message : 'payroll');
    }

    try {
      const { fetchProjectManagerProjects, pmProjectLabel } = await import('./projectManagerBridge');
      const pmRows = await fetchProjectManagerProjects();
      if (pmRows.length) {
        return pmRows.map((p) => ({
          id: p.id,
          label: pmProjectLabel(p),
          status: p.status || '',
          progress: 0,
        }));
      }
    } catch (e) {
      errors.push(e instanceof Error ? e.message : 'project-manager');
    }

    try {
      const lookups = await this.lookups();
      if (lookups.projects?.length) return lookups.projects;
    } catch (e) {
      errors.push(e instanceof Error ? e.message : 'lookups');
    }

    if (errors.length) {
      throw new Error(errors[0] || 'خطا در دریافت لیست پروژه‌ها');
    }
    return [];
  },

  async expenseTypes(): Promise<LookupExpenseType[]> {
    const res = await getJson<{ success: boolean; data: LookupExpenseType[] }>('/expense-types');
    return res.data;
  },

  async project(projectId: number): Promise<LookupProject> {
    const res = await getJson<{ success: boolean; data: LookupProject }>(`/projects/${projectId}`);
    return unwrapList<LookupProject>(res);
  },

  async projectDocumentLines(
    projectId: number,
    params?: {
      expense_type_id?: number;
      from_date?: string;
      to_date?: string;
      beneficiary?: string;
    },
  ): Promise<ProjectDocumentLineRow[]> {
    const qs = new URLSearchParams();
    if (params?.expense_type_id) qs.set('expense_type_id', String(params.expense_type_id));
    if (params?.from_date) qs.set('from_date', params.from_date);
    if (params?.to_date) qs.set('to_date', params.to_date);
    if (params?.beneficiary) qs.set('beneficiary', params.beneficiary);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await getJson<{ success: boolean; data: ProjectDocumentLineRow[] }>(
      `/projects/${projectId}/document-lines${suffix}`,
    );
    const data = unwrapList<ProjectDocumentLineRow[]>(res);
    return Array.isArray(data) ? data : [];
  },

  async projectAccountingDocuments(projectId: number): Promise<ProjectAccountingDocument[]> {
    const res = await getJson<{ success: boolean; data: ProjectAccountingDocument[] }>(
      `/projects/${projectId}/documents`,
    );
    const data = unwrapList<ProjectAccountingDocument[]>(res);
    return Array.isArray(data) ? data : [];
  },

  async projectBankStatements(projectId: number): Promise<ProjectBankStatementsResponse> {
    const res = await getJson<{ success: boolean; data: ProjectBankStatementsResponse }>(
      `/projects/${projectId}/bank-statements`,
    );
    const data = unwrapList<ProjectBankStatementsResponse>(res);
    return {
      items: data?.items || [],
      summary: data?.summary || { total_credit: 0, total_debit: 0, count: 0 },
    };
  },

  async projectAdvances(projectId: number): Promise<ProjectAdvancesResponse> {
    const res = await getJson<{ success: boolean; data: ProjectAdvancesResponse }>(
      `/projects/${projectId}/advances`,
    );
    const data = unwrapList<ProjectAdvancesResponse>(res);
    return {
      items: data?.items || [],
      summary: data?.summary || { total_amount: 0, count: 0 },
    };
  },

  async expenseTypesReport(params?: {
    project_id?: number;
    expense_type_id?: number;
    from_date?: string;
    to_date?: string;
  }): Promise<{ summary: ExpenseTypeReportSummaryRow[]; entries: ExpenseTypeReportEntryRow[] }> {
    const qs = new URLSearchParams();
    if (params?.project_id) qs.set('project_id', String(params.project_id));
    if (params?.expense_type_id) qs.set('expense_type_id', String(params.expense_type_id));
    if (params?.from_date) qs.set('from_date', params.from_date);
    if (params?.to_date) qs.set('to_date', params.to_date);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await getJson<{
      success: boolean;
      data: { summary: ExpenseTypeReportSummaryRow[]; entries: ExpenseTypeReportEntryRow[] };
    }>(`/expense-types/report${suffix}`);
    return res.data;
  },

  async createExpenseType(payload: ExpenseTypePayload): Promise<{ id: number }> {
    const res = await sendJson<{ success: boolean; data: { id: number } }>('POST', '/expense-types', payload);
    return res.data;
  },

  async updateExpenseType(id: number, payload: ExpenseTypePayload): Promise<void> {
    await sendJson<{ success: boolean }>('PUT', `/expense-types/${id}`, payload);
  },

  async deleteExpenseType(id: number): Promise<void> {
    await sendJson<{ success: boolean }>('DELETE', `/expense-types/${id}`);
  },

  async bankAccounts(): Promise<LookupBankAccount[]> {
    const res = await getJson<{ success: boolean; data: LookupBankAccount[] }>('/bank-accounts');
    return res.data;
  },

  async createBankAccount(payload: BankAccountPayload): Promise<LookupBankAccount> {
    const res = await sendJson<{ success: boolean; data: LookupBankAccount }>('POST', '/bank-accounts', payload);
    return res.data;
  },

  async updateBankAccount(id: number, payload: BankAccountPayload): Promise<LookupBankAccount> {
    const res = await sendJson<{ success: boolean; data: LookupBankAccount }>('PUT', `/bank-accounts/${id}`, payload);
    return res.data;
  },

  async deleteBankAccount(id: number): Promise<void> {
    await sendJson<{ success: boolean }>('DELETE', `/bank-accounts/${id}`);
  },

  async adjustBankBalance(
    id: number,
    payload: { amount: number; direction: 'debit' | 'credit'; description?: string },
  ): Promise<{ id: number; current_balance: number }> {
    const res = await sendJson<{ success: boolean; data: { id: number; current_balance: number } }>(
      'POST',
      `/bank-accounts/${id}/adjust-balance`,
      payload,
    );
    return res.data;
  },

  async setDefaultDestination(id: number): Promise<void> {
    await sendJson<{ success: boolean }>('POST', `/bank-accounts/${id}/set-default-destination`);
  },

  async bankAccount(id: number): Promise<LookupBankAccount> {
    const res = await getJson<{ success: boolean; data: LookupBankAccount }>(`/bank-accounts/${id}`);
    return res.data;
  },

  async bankAccountTransactions(id: number): Promise<BankAccountTransaction[]> {
    const res = await getJson<{ success: boolean; data: BankAccountTransaction[] }>(
      `/bank-accounts/${id}/transactions`,
    );
    return res.data;
  },

  async bankMovements(): Promise<AccountingBankMovementRecord[]> {
    const res = await getJson<{ success: boolean; data: AccountingBankMovementRecord[] }>('/bank-movements');
    return res.data;
  },

  async createBankMovement(payload: {
    direction: 'incoming' | 'outgoing' | 'transfer';
    amount: number;
    value_date?: string;
    source_bank_account_id?: number | null;
    destination_bank_account_id?: number | null;
    counterparty_type?: 'employee' | 'client' | 'company';
    counterparty_id?: number | null;
    counterparty_bank_account_id?: number | null;
    payment_method?: 'card' | 'sheba' | 'account' | 'cash' | 'cheque';
    reference_no?: string;
    description?: string;
  }): Promise<{ id: string }> {
    const res = await sendJson<{ success: boolean; data: { id: string } }>('POST', '/bank-movements', payload);
    return res.data;
  },

  async beneficiaryBankAccounts(beneficiaryId: string): Promise<BeneficiaryBankAccount[]> {
    const res = await getJson<{ success: boolean; data: BeneficiaryBankAccount[] }>(
      `/beneficiaries/${beneficiaryId}/bank-accounts`,
    );
    return res.data;
  },

  async createBeneficiaryBankAccount(
    beneficiaryId: string,
    payload: {
      account_holder_name: string;
      bank_name?: string;
      account_number?: string;
      card_number?: string;
      sheba_number?: string;
      is_default?: boolean;
      is_active?: boolean;
    },
  ): Promise<void> {
    await sendJson<{ success: boolean }>('POST', `/beneficiaries/${beneficiaryId}/bank-accounts`, payload);
  },

  async employees(params?: { search?: string }): Promise<EmployeeRecord[]> {
    const qs = new URLSearchParams();
    if (params?.search?.trim()) qs.set('search', params.search.trim());
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await getJson<{ success: boolean; data: EmployeeRecord[] }>(`/employees${suffix}`);
    return res.data;
  },

  async createEmployee(payload: EmployeePayload): Promise<{ id: number }> {
    const res = await sendJson<{ success: boolean; data: { id: number } }>('POST', '/employees', payload);
    return res.data;
  },

  async updateEmployee(id: number, payload: EmployeePayload): Promise<void> {
    await sendJson<{ success: boolean }>('PUT', `/employees/${id}`, payload);
  },

  async employeeAccountingProfile(
    employeeId: number,
    params?: { project_id?: number; from_date?: string; to_date?: string },
  ): Promise<EmployeeAccountingProfile> {
    const qs = new URLSearchParams();
    if (params?.project_id) qs.set('project_id', String(params.project_id));
    if (params?.from_date?.trim()) qs.set('from_date', params.from_date.trim());
    if (params?.to_date?.trim()) qs.set('to_date', params.to_date.trim());
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await getJson<{ success: boolean; data: EmployeeAccountingProfile }>(
      `/employees/${employeeId}/accounting-profile${suffix}`,
    );
    return res.data;
  },

  async employeeBankAccounts(
    employeeId: number,
  ): Promise<{ bank_accounts: EmployeeBankAccountRecord[]; banks: BankOption[] }> {
    const res = await getJson<{
      success: boolean;
      data: { bank_accounts: EmployeeBankAccountRecord[]; banks: BankOption[] };
    }>(`/employees/${employeeId}/bank-accounts`);
    return res.data;
  },

  async createEmployeeBankAccount(employeeId: number, payload: EmployeeBankAccountPayload): Promise<void> {
    await sendJson<{ success: boolean }>(
      'POST',
      `/employees/${employeeId}/bank-accounts`,
      payload,
    );
  },

  async updateEmployeeBankAccount(
    employeeId: number,
    accountId: number,
    payload: EmployeeBankAccountPayload,
  ): Promise<void> {
    await sendJson<{ success: boolean }>(
      'PUT',
      `/employees/${employeeId}/bank-accounts/${accountId}`,
      payload,
    );
  },

  async deleteEmployeeBankAccount(employeeId: number, accountId: number): Promise<void> {
    await sendJson<{ success: boolean }>(
      'DELETE',
      `/employees/${employeeId}/bank-accounts/${accountId}`,
    );
  },

  async setDefaultEmployeeBankAccount(employeeId: number, accountId: number): Promise<void> {
    await sendJson<{ success: boolean }>(
      'POST',
      `/employees/${employeeId}/bank-accounts/${accountId}/set-default`,
    );
  },

  async detectBankFromCard(cardNumber: string): Promise<CardBankInfo> {
    const res = await sendJson<{ success: boolean; data: CardBankInfo }>(
      'POST',
      '/employees/get-bank-info-from-card',
      { card_number: cardNumber },
    );
    return res.data;
  },

  async fillEmployeeBankAccountIban(
    employeeId: number,
    accountId: number,
  ): Promise<{
    id: number;
    iban?: string;
    account_number?: string | null;
    already_had_iban?: boolean;
    account_number_filled?: boolean;
    owner?: string | null;
  }> {
    const res = await sendJson<{
      success: boolean;
      message?: string;
      data: {
        id: number;
        iban?: string;
        account_number?: string | null;
        already_had_iban?: boolean;
        account_number_filled?: boolean;
        owner?: string | null;
      };
    }>('POST', `/employees/${employeeId}/bank-accounts/${accountId}/fill-iban`);
    return res.data;
  },

  async accountingHeadingsTree(): Promise<AccountingHeadingNode[]> {
    return [];
  },

  async accountingHeadingsList(params?: {
    level?: 'general' | 'subsidiary' | 'detail' | 'project';
    parent_id?: number;
    search?: string;
  }): Promise<AccountingHeadingListItem[]> {
    // فقط سطح project برای انتخاب پروژه در اسناد باقی مانده است.
    if (params?.level !== 'project') return [];
    const qs = new URLSearchParams();
    qs.set('level', 'project');
    if (params?.search?.trim()) qs.set('search', params.search.trim());
    const res = await getJson<{ success: boolean; data: AccountingHeadingListItem[] }>(
      `/accounting-headings/list?${qs.toString()}`,
    );
    return Array.isArray(res.data) ? res.data : [];
  },

  async accountingSubsidiaries(params?: { entity_type?: 'employee' | 'client' | 'company' | 'project' }): Promise<AccountingSubsidiary[]> {
    const qs = new URLSearchParams();
    if (params?.entity_type) qs.set('entity_type', params.entity_type);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const res = await getJson<{ success: boolean; data: AccountingSubsidiary[] }>(`/accounting-subsidiaries${suffix}`);
    return res.data;
  },

  async accountingDocuments(): Promise<AccountingDocumentRecord[]> {
    const res = await getJson<{ success: boolean; data: AccountingDocumentRecord[] }>('/accounting-documents');
    return res.data;
  },

  async nextAccountingDocumentNumber(): Promise<string> {
    const res = await getJson<{ success: boolean; data: { next_doc_no: string } }>('/accounting-documents/next-number');
    return res.data.next_doc_no;
  },

  async accountingDocument(id: string): Promise<AccountingDocumentRecord> {
    const res = await getJson<{ success: boolean; data: AccountingDocumentRecord }>(`/accounting-documents/${id}`);
    return res.data;
  },

  async createAccountingDocument(payload: {
    doc_date: string;
    operation_description?: string;
    rows: AccountingDocumentRowPayload[];
  }): Promise<{ id: string; docNo: string; verificationCode?: string }> {
    const res = await sendJson<{ success: boolean; data: { id: string; docNo: string; verificationCode?: string } }>(
      'POST',
      '/accounting-documents',
      payload,
    );
    return res.data;
  },

  async updateAccountingDocument(
    id: string,
    payload: {
      doc_date: string;
      operation_description?: string;
      rows: AccountingDocumentRowPayload[];
    },
  ): Promise<{ id: string; docNo: string; verificationCode?: string }> {
    const res = await sendJson<{ success: boolean; data: { id: string; docNo: string; verificationCode?: string } }>(
      'PUT',
      `/accounting-documents/${id}`,
      payload,
    );
    return res.data;
  },

  async payrollCalculatorProjects(month: string): Promise<{
    month: string;
    month_label: string;
    projects: PayrollCalculatorProjectOption[];
  }> {
    const qs = new URLSearchParams();
    if (month) qs.set('month', month);
    const res = await getJson<{
      success: boolean;
      data: { month: string; month_label: string; projects: PayrollCalculatorProjectOption[] };
    }>(`/payroll-calculator/projects?${qs.toString()}`);
    return res.data;
  },

  async payrollCalculatorPreview(params: {
    month: string;
    project_id: number[];
  }): Promise<PayrollCalculatorPreview> {
    const qs = new URLSearchParams();
    qs.set('month', params.month);
    params.project_id.forEach((id) => qs.append('project_id[]', String(id)));
    const res = await getJson<{ success: boolean; data: PayrollCalculatorPreview }>(
      `/payroll-calculator/preview?${qs.toString()}`,
    );
    return res.data;
  },

  async accountingAccountReport(params: {
    account_code: string;
    from_date?: string;
    to_date?: string;
  }): Promise<AccountingAccountReport> {
    const qs = new URLSearchParams();
    qs.set('account_code', params.account_code);
    if (params.from_date) qs.set('from_date', params.from_date);
    if (params.to_date) qs.set('to_date', params.to_date);
    const res = await getJson<{ success: boolean; data: AccountingAccountReport }>(
      `/accounting-documents/account-report?${qs.toString()}`,
    );
    return res.data;
  },
};
